import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';

// The 'api.js' file should contain all your fetch functions
import {
  fetchConnections,
  fetchOqlousTools,
  fetchGlobalConnections,
} from '../components/McpConnections/api';
import { mayCall } from '../services/access';
import {
  getAppResources,
  getAuthenticatedApps,
  API_URL,
} from '../services/api';
import {
  ENABLE_INTEGRATION_JIRA,
  ENABLE_INTEGRATION_MICROSOFT_TEAMS,
  ENABLE_INTEGRATION_CONFLUENCE,
  ENABLE_INTEGRATION_GOHIGHLEVEL,
} from '../config/features';
// Assuming DataState provides the user's auth token
import { DataState } from './DataProvider';

const McpContext = createContext({
  connections: [],
  setConnections: () => {},
  isLoading: false,
  setIsLoading: () => {},
  error: null,
  setError: () => {},
  loadConnections: () => {},
  refreshConnections: () => {},
  oqlousTools: [],
  setOqlousTools: () => {},
  checkResult: null,
  setCheckResult: () => {},
  globalConnections: [],
  appConfigs: [],
  setAppConfigs: () => {},
});

export const McpProvider = ({ children }) => {
  const [connections, setConnections] = useState([]);
  const [oqlousTools, setOqlousTools] = useState([]);
  const [globalConnections, setGlobalConnections] = useState([]); // NEW: State for global connections
  const [isLoading, setIsLoading] = useState(true); // Set initial loading to true
  const [error, setError] = useState(null);
  const [checkResult, setCheckResult] = useState(null);

  const [appConfigs, setAppConfigs] = useState({
    jira: null,
    confluence: null,
    microsoft_teams: null,
    gohighlevel: null,
  });

  // NEW: State to trigger a refresh. Incrementing it will cause the useEffect to re-run.
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const { userGoogleId: accessToken } = DataState();

  // COMBINED loading function for better performance and state management
  const loadAllMcpData = useCallback(async () => {
    if (!accessToken) {
      // Clear all data on logout
      setConnections([]);
      setOqlousTools([]);
      setGlobalConnections([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const [connectionsData, toolsData, globalData] = await Promise.all([
        fetchConnections(accessToken).catch(() => []),
        fetchOqlousTools(accessToken).catch(() => []),
        fetchGlobalConnections(accessToken).catch(() => []),
      ]);

      setConnections(connectionsData || []);
      setOqlousTools(toolsData || []);
      setGlobalConnections(globalData || []);

      // Pre-fetch app configs non-blockingly, for connected apps only: the
      // server refuses a resources call for an app that is not connected.
      // If the connected list cannot be read, ask for every app as before.
      const connectedApps = getAuthenticatedApps(accessToken)
        .then((response) => response?.apps || [])
        .catch(() => null);
      const isConnected = async (app) => {
        const apps = await connectedApps;
        return !apps || apps.includes(app);
      };
      const whenConnected = async (app, load) =>
        (await isConnected(app)) ? load() : undefined;

      Promise.allSettled([
        ...(ENABLE_INTEGRATION_JIRA
          ? [
              whenConnected('jira', () =>
                getAppResources('jira', accessToken).then((d) =>
                  setAppConfigs((p) => ({ ...p, jira: d })),
                ),
              ),
            ]
          : []),
        ...(ENABLE_INTEGRATION_CONFLUENCE
          ? [
              whenConnected('confluence', () =>
                getAppResources('confluence', accessToken).then((d) =>
                  setAppConfigs((p) => ({ ...p, confluence: d })),
                ),
              ),
            ]
          : []),
        ...(ENABLE_INTEGRATION_MICROSOFT_TEAMS &&
        mayCall(`${API_URL}/microsoft_teams/resources`)
          ? [
              whenConnected('microsoft_teams', () =>
                fetch(`${API_URL}/microsoft_teams/resources`, {
                  headers: { Authorization: `Bearer ${accessToken}` },
                  credentials: 'include',
                })
                  .then((r) => r.json())
                  .then((j) => {
                    if (j.success)
                      setAppConfigs((p) => ({ ...p, microsoft_teams: j.data }));
                  }),
              ),
            ]
          : []),
        ...(ENABLE_INTEGRATION_GOHIGHLEVEL
          ? [
              fetch(`${API_URL}/gohighlevel/resources`, {
                headers: { Authorization: `Bearer ${accessToken}` },
                credentials: 'include',
              })
                .then((r) => r.json())
                .then((j) => {
                  if (j.success)
                    setAppConfigs((p) => ({ ...p, gohighlevel: j.data }));
                }),
            ]
          : []),
      ]);
    } catch (err) {
      setError('Failed to load connection data.');
    } finally {
      // Set loading to false only once after all fetches are complete
      setIsLoading(false);
    }
  }, [accessToken]); // This function only changes when the access token changes

  // BEST PRACTICE FIX: The loadConnections function depends on 'accessToken'.
  // It MUST be included in the dependency array of useCallback.
  // Otherwise, it will forever use the initial value of accessToken (a "stale closure").
  const loadConnections = useCallback(async () => {
    // Don't try to fetch if the access token isn't available yet.
    if (!accessToken) {
      setConnections([]); // Ensure data is cleared on logout
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      setIsLoading(true);
      const response = await fetchConnections(accessToken);
      setConnections(response || []); // Handle cases where the API might not return an array
    } catch (err) {
      setError('Failed to load connections.');
    } finally {
      setIsLoading(false);
    }
  }, [accessToken]); // FIX: Added accessToken to the dependency array.

  useEffect(() => {
    loadAllMcpData();
  }, [loadAllMcpData, refreshTrigger]); // UPDATE: Added refreshTrigger as a dependency.

  // NEW: A stable function to be called by child components to trigger a refresh.
  // useCallback with an empty dependency array ensures this function itself never changes.
  const refreshConnections = useCallback(() => {
    setRefreshTrigger((prev) => prev + 1);
  }, []);

  return (
    <McpContext.Provider
      value={{
        connections,
        setConnections,
        isLoading,
        setIsLoading,
        error,
        setError,
        loadConnections,
        refreshConnections,
        oqlousTools,
        setOqlousTools,
        checkResult,
        setCheckResult,
        globalConnections, // NEW: Expose global connections in the context
        appConfigs,
        setAppConfigs,
      }}
    >
      {children}
    </McpContext.Provider>
  );
};

// Custom hook for easy consumption.
export const useMCP = () => useContext(McpContext);
